import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import Button from '../../shared/button.component';
import { withPermissions, permissions } from '../../shared/authorization';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import FormControl from '../../shared/forms/formControl.component';
import FormRadioInput from '../../shared/forms/formRadioInput.component';
import FormHelpers from '../../shared/forms/formHelpers';
import ConfirmDialog from '../../shared/confirmDialog.component';

import { getFormDataState } from './applicationSettings.initialState';
import {
  fetchApplicationSettingsFlags,
  updateApplicationSettingsFlags,
  clearDevCache,
  materializedViewRefresh,
  unlockAllTaxEntries,
  fetchAdminJobs,
  runAdminJob,
  runDeactivateUsersJob,
  clearBsmBalances,
  selectPeriods,
  selectCompanyCodes,
} from './store/actions';
import {
  adminJobsForDroplistSelector,
  adminJobInProgressNameSelector,
  adminJobsFetchingSelector,
  deactivateUsersJobInProgressSelector,
  companyCodesForDroplistSelector,
  selectedCompanyCodesSelector,
  periodsForDroplistSelector,
  selectedPeriodsSelector,
  clearBsmBalancesInProgressSelector,
  lastClientSelector,
} from './store/selectors';
import { environmentSelector } from '../../shared/store/selectors';
import { ApplicationSettingsFlagsPropTypes } from './propTypes';
import AdminJobs from './adminJobs.component';
import { OptionsPropType } from '../../shared/propTypes/common';
import TruncatableSelect from '../../shared/reactSelect/truncatableSelect.component';
import PasteCreatable from '../../shared/reactSelect/pasteCreatable.component';
import { testRedisConnection as testRedisConnectionApi } from './api';

class ApplicationSettingsPage extends PureComponent {
  static propTypes = {
    applicationSettingsFlags: ApplicationSettingsFlagsPropTypes,
    applicationSettingsFlagsLoading: PropTypes.bool.isRequired,
    applicationSettingsFlagsUpdating: PropTypes.bool.isRequired,

    fetchApplicationSettingsFlags: PropTypes.func.isRequired,
    updateApplicationSettingsFlags: PropTypes.func.isRequired,
    clearDevCache: PropTypes.func.isRequired,
    devCacheClearing: PropTypes.bool.isRequired,
    tlsEnv: PropTypes.string.isRequired,

    materializedViewRefresh: PropTypes.func.isRequired,
    materializedViewRefreshLoading: PropTypes.bool.isRequired,

    unlockAllTaxEntries: PropTypes.func.isRequired,
    unlockAllTaxEntriesLoading: PropTypes.bool.isRequired,
    fetchAdminJobs: PropTypes.func.isRequired,
    runAdminJob: PropTypes.func.isRequired,
    runDeactivateUsersJob: PropTypes.func.isRequired,
    deactivateUsersJobInProgress: PropTypes.bool.isRequired,
    adminJobInProgressName: PropTypes.string,
    adminJobsFetching: PropTypes.bool.isRequired,
    adminJobs: OptionsPropType,

    selectPeriods: PropTypes.func,
    selectCompanyCodes: PropTypes.func,
    companyCodes: OptionsPropType,
    selectedCompanyCodes: OptionsPropType,
    periods: OptionsPropType,
    selectedPeriods: OptionsPropType,
    lastClient: PropTypes.string,
    clearBsmBalances: PropTypes.func,
    clearBsmBalancesInProgress: PropTypes.bool,
  };

  state = {
    formData: getFormDataState(),
    isDialogVisible: false,
    selectedPeriod: {
      value: '',
      taxYear: '',
    },
    isFormDirty: false,
    redisTestLoading: false,
    redisTestResult: null,
  };

  componentDidMount() {
    const { fetchApplicationSettingsFlags, fetchAdminJobs } = this.props;

    fetchApplicationSettingsFlags();
    fetchAdminJobs();
  }

  componentDidUpdate() {
    if (!this.props.applicationSettingsFlags) {
      return;
    }

    const {
      maintenanceFlag,
    } = this.props.applicationSettingsFlags;

    if (!this.state.formData.isInitialised) {
      this.setState({
        formData: getFormDataState({
          maintenanceFlag,
        }),
        isFormDirty: false,
      });
    }
  }

  resetApplicationSettingsFlags = () => {
    if (!this.props.applicationSettingsFlags) {
      return;
    }

    const {
      maintenanceFlag,
    } = this.props.applicationSettingsFlags;

    FormHelpers.resetForm({
      initialState: {
        ...getFormDataState({
          maintenanceFlag,
        }),
      },
      setState: this.setState.bind(this),
    });
  };

  submitApplicationSettingsFlags = async () => {
    const { formData } = this.state;

    await this.props.updateApplicationSettingsFlags(this.getFieldValues(formData));
    this.resetApplicationSettingsFlags();
  };

  changeProperty = event => {
    FormHelpers.changeProperty({
      event,
      setState: this.setState.bind(this),
    });
  };

  getFieldValues = formData => ({
    maintenanceFlag: formData.maintenanceFlag.value,
  });

  selectCompanyCodes = selectedCompanyCodes => {
    const { selectCompanyCodes } = this.props;
    const companyCodesToSelect = this.props.companyCodes.filter(
      ({ value }) => selectedCompanyCodes.some(
        selectedCompanyCode => selectedCompanyCode.value.toLowerCase() === value.toLowerCase()
      )
    );
    selectCompanyCodes(companyCodesToSelect);
  };

  selectPeriods = selectedPeriods => {
    const { selectPeriods } = this.props;
    const periodsToSelect = this.props.periods.filter(
      ({ value }) => selectedPeriods.some(
        selectedPeriod => selectedPeriod.value.toLowerCase() === value.toLowerCase()
      )
    );
    selectPeriods(periodsToSelect);
  };

  selectPeriod = selectedPeriod => this.setState({
    selectedPeriod: !selectedPeriod ? '' : { value: selectedPeriod.value, taxYear: selectedPeriod.taxYear },
  });

  showDialog = () => {
    const { selectedCompanyCodes, selectedPeriods } = this.props;
    if (selectedCompanyCodes.length > 0 && selectedPeriods.length > 0) {
      this.setState({ isDialogVisible: true });
    }
  };

  hideDialog = () => this.setState({
    isDialogVisible: false,
  });

  getMessage = () => {
    const { periods, selectedCompanyCodes, selectedPeriods, tlsEnv, lastClient } = this.props;
    const [formattedCompanyCode, formattedDemonstrative] = selectedCompanyCodes.length === 1 ? ['company code', 'this'] : ['company codes', 'these'];
    const formattedPeriod = selectedPeriods.length === 1 ? 'period' : 'periods';
    const formattedSelectedCompanyCodes = selectedCompanyCodes.map(companyCode => companyCode.value).join(', ');
    const formattedSelectedPeriods = selectedPeriods.length === periods.length ? 'all' : selectedPeriods.map(period => period.value).join(', ');

    return `Are you sure you want to clear BSM balances for ${formattedSelectedCompanyCodes} ${formattedCompanyCode} for ${formattedSelectedPeriods} ${formattedPeriod}?
    Clearing BSM balances will remove all BSM data for ${formattedDemonstrative} ${formattedCompanyCode} and all trading partners associated
    on ${tlsEnv} for ${lastClient} client.`;
  };

  runRedisTest = async () => {
    this.setState({ redisTestLoading: true, redisTestResult: null });
    try {
      const result = await testRedisConnectionApi();
      this.setState({ redisTestLoading: false, redisTestResult: result });
    } catch (e) {
      this.setState({
        redisTestLoading: false,
        redisTestResult: { success: false, error: e.message, steps: [] },
      });
    }
  };

  clearBsmBalances = () => {
    const { periods, selectedCompanyCodes, selectedPeriods, clearBsmBalances } = this.props;
    const companyCodesList = selectedCompanyCodes.map(companyCode => companyCode.value).join(',');
    const periodsList = periods.length === selectedPeriods.length ? 'ALL' : selectedPeriods.map(period => period.value).join(',');
    if (selectedCompanyCodes.length > 0 && selectedPeriods.length > 0) {
      clearBsmBalances(companyCodesList, periodsList);
    }
    this.hideDialog();
  };

  runDeactivateUsersJob = () => {
    this.props.runDeactivateUsersJob();
  };

  render() {
    const {
      formData,
      isDialogVisible,
      isFormDirty,
    } = this.state;

    const {
      applicationSettingsFlagsLoading,
      applicationSettingsFlagsUpdating,
      devCacheClearing,
      clearDevCache,
      materializedViewRefresh,
      materializedViewRefreshLoading,
      unlockAllTaxEntries,
      unlockAllTaxEntriesLoading,
      adminJobs,
      adminJobInProgressName,
      adminJobsFetching,
      runAdminJob,
      deactivateUsersJobInProgress,
      tlsEnv,
      companyCodes,
      selectedCompanyCodes,
      periods,
      selectedPeriods,
      clearBsmBalancesInProgress,
    } = this.props;

    const { redisTestLoading, redisTestResult } = this.state;

    const applicationFlagControlsDisabled = applicationSettingsFlagsLoading || applicationSettingsFlagsUpdating;
    const filteredCompanyCodes = companyCodes.filter(companyCode => companyCode);

    return (
      <div className="application-settings-page">
        <NavigationPrompt show={isFormDirty}/>
        <div className="container-fluid">
          <div className="page-without-secondary-menu">
            <div className="application-settings-form page-content-container">
              <div className="row">
                <div className="col">
                  <h5 className="application-settings-title">Application Settings</h5>
                  <div className="row">
                    <div className="col">
                      {tlsEnv === 'LOCAL_DEV'
                        ? (
                          <div className="form-group row align-items-center">
                            <label className="col-3 col-form-label">Clear DEV_CACHE_RESPONSES cache</label>
                            <div className="col-9">
                              <Button
                                type="button"
                                kind="primary"
                                size="lg"
                                onClick={clearDevCache}
                                isLoading={devCacheClearing}
                              >
                                Clear DEV cache
                              </Button>
                            </div>
                          </div>
                        )
                        : null}
                      <FormControl
                        label="Maintenance Flag"
                        name="maintenanceFlag"
                        horizontal
                        value={formData.maintenanceFlag.value}
                        onChange={this.changeProperty}
                      >
                        <FormRadioInput disabled={applicationFlagControlsDisabled}/>
                      </FormControl>
                      <div className="form-group row align-items-center">
                        <label className="col-3 col-form-label">Materialized View Refresh</label>
                        <div className="col-9">
                          <Button
                            type="button"
                            kind="primary"
                            size="lg"
                            onClick={materializedViewRefresh}
                            isLoading={materializedViewRefreshLoading}
                          >
                            Materialized View Refresh
                          </Button>
                        </div>
                      </div>
                      <div className="form-group row align-items-center">
                        <ConfirmDialog
                          onCancel={this.hideDialog}
                          onConfirm={this.clearBsmBalances}
                          message={`${this.getMessage()}`}
                          title="Confirm deletion"
                          dismissText="Cancel"
                          submitText="Delete"
                          isVisible={isDialogVisible}
                        />
                        <label className="col-3 col-form-label">Clear BSM Balances</label>
                        <div className="col-4">
                          <TruncatableSelect
                            clearable
                            multi
                            removeSelected
                            virtualized
                            closeOnSelect={false}
                            onChange={this.selectCompanyCodes}
                            selectComponentClassName="form-control"
                            options={filteredCompanyCodes}
                            value={selectedCompanyCodes}
                            placeholder="Select Company Codes..."
                            disabled={clearBsmBalancesInProgress}
                            selectComponent={PasteCreatable}
                          />
                        </div>
                        <div className="col-4">
                          <TruncatableSelect
                            clearable
                            multi
                            removeSelected
                            virtualized
                            withAll
                            closeOnSelect={false}
                            onChange={this.selectPeriods}
                            selectComponentClassName="form-control"
                            options={periods}
                            value={selectedPeriods}
                            placeholder="Select Periods..."
                            disabled={clearBsmBalancesInProgress}
                            selectComponent={PasteCreatable}
                          />
                        </div>
                        <div className="col-1">
                          <Button
                            type="button"
                            kind="primary"
                            size="lg"
                            isLoading={clearBsmBalancesInProgress}
                            onClick={this.showDialog}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                      <div className="form-group row align-items-center">
                        <label className="col-3 col-form-label">Unlock all tax entries</label>
                        <div className="col-9">
                          <Button
                            type="button"
                            kind="primary"
                            size="lg"
                            onClick={unlockAllTaxEntries}
                            isLoading={unlockAllTaxEntriesLoading}
                          >
                            Unlock
                          </Button>
                        </div>
                      </div>
                      <AdminJobs
                        adminJobs={adminJobs}
                        adminJobsFetching={adminJobsFetching}
                        adminJobInProgressName={adminJobInProgressName}
                        runAdminJob={runAdminJob}
                        tlsEnv={tlsEnv}
                      />
                      <div className="form-group row align-items-center">
                        <label className="col-3 col-form-label">Deactivate Users Job</label>
                        <div className="col-9">
                          <Button
                            type="button"
                            kind="primary"
                            size="lg"
                            onClick={this.runDeactivateUsersJob}
                            isLoading={deactivateUsersJobInProgress}
                          >
                            Run Deactivate Users Job
                          </Button>
                        </div>
                      </div>
                      <div className="form-group row align-items-start">
                        <label className="col-3 col-form-label">Azure Redis Connection Test</label>
                        <div className="col-9">
                          <Button
                            type="button"
                            kind="primary"
                            size="lg"
                            onClick={this.runRedisTest}
                            isLoading={redisTestLoading}
                          >
                            Test Redis Connection
                          </Button>
                          {redisTestResult && (
                            <div className="w-100 mt-2">
                              <div className={`alert ${redisTestResult.success ? 'alert-success' : 'alert-danger'}`}>
                                <strong>{redisTestResult.success ? 'OK' : 'Error'}</strong>
                                {redisTestResult.error && `: ${redisTestResult.error}`}
                              </div>
                              <details className="mt-2">
                                <summary>Log</summary>
                                <pre className="bg-light p-3 mt-2 rounded small" style={{ maxHeight: '300px', overflow: 'auto' }}>
                                  {JSON.stringify(redisTestResult.steps, null, 2)}
                                </pre>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col">
                  <Button
                    type="button"
                    kind="primary"
                    className="a-mx-10 float-right submit-application-settings-flag-button"
                    onClick={this.submitApplicationSettingsFlags}
                    disabled={applicationFlagControlsDisabled}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    kind="secondary"
                    className="float-right"
                    onClick={this.resetApplicationSettingsFlags}
                    disabled={applicationFlagControlsDisabled}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { applicationSettings } = state.administration;

  return {
    applicationSettingsFlags: applicationSettings.applicationSettingsFlags,
    applicationSettingsFlagsLoading: applicationSettings.applicationSettingsFlagsLoading,
    applicationSettingsFlagsUpdating: applicationSettings.applicationSettingsFlagsUpdating,
    devCacheClearing: applicationSettings.devCacheClearing,
    materializedViewRefreshLoading: applicationSettings.materializedViewRefreshLoading,
    unlockAllTaxEntriesLoading: applicationSettings.unlockAllTaxEntriesLoading,

    adminJobs: adminJobsForDroplistSelector(state),
    tlsEnv: environmentSelector(state),
    adminJobInProgressName: adminJobInProgressNameSelector(state),
    adminJobsFetching: adminJobsFetchingSelector(state),
    companyCodes: companyCodesForDroplistSelector(state),
    selectedCompanyCodes: selectedCompanyCodesSelector(state),
    periods: periodsForDroplistSelector(state),
    selectedPeriods: selectedPeriodsSelector(state),
    lastClient: lastClientSelector(state),
    clearBsmBalancesInProgress: clearBsmBalancesInProgressSelector(state),
    deactivateUsersJobInProgress: deactivateUsersJobInProgressSelector(state),
  };
}

export default withPermissions(permissions.APPLICATION_SETTINGS)(connect(
  mapStateToProps,
  {
    fetchApplicationSettingsFlags,
    updateApplicationSettingsFlags,
    clearDevCache,
    materializedViewRefresh,
    unlockAllTaxEntries,
    fetchAdminJobs,
    runAdminJob,
    runDeactivateUsersJob,
    clearBsmBalances,
    selectCompanyCodes,
    selectPeriods,
  }
)(ApplicationSettingsPage));
